/**
 * A/B Testing API Client
 * Story: E007-S011 - A/B Test Manager
 */

import { apiClient } from './apiClient';
import type {
  ABTest,
  ABTestListItem,
  CreateTestRequest,
  DeclareWinnerRequest,
  TestTemplate
} from '../types/ab-tests';

export const abTestsApi = {
  /**
   * Get all A/B tests with optional filtering
   */
  async list(params?: {
    status?: 'active' | 'completed' | 'stopped';
    limit?: number;
    offset?: number;
  }): Promise<{ tests: ABTestListItem[]; total: number; limit: number; offset: number }> {
    const response = await apiClient.get('/ab-tests', { params });
    return response.data;
  },

  /**
   * Get a single A/B test with full details
   */
  async get(testId: string): Promise<ABTest> {
    const response = await apiClient.get(`/ab-tests/${testId}`);
    return response.data;
  },

  /**
   * Create a new A/B test
   */
  async create(data: CreateTestRequest): Promise<{ success: boolean; test: any }> {
    const response = await apiClient.post('/ab-tests', data);
    return response.data;
  },

  /**
   * Declare a winner for a test (manual or triggered)
   */
  async declareWinner(testId: string, data: DeclareWinnerRequest): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post(`/ab-tests/${testId}/declare-winner`, data);
    return response.data;
  },

  /**
   * Stop a test early
   */
  async stop(testId: string): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.delete(`/ab-tests/${testId}`);
    return response.data;
  },

  /**
   * Get test templates for quick setup
   */
  async getTemplates(): Promise<{ templates: TestTemplate[]; count: number; note: string }> {
    const response = await apiClient.get('/ab-tests/templates/list');
    return response.data;
  },

  /**
   * Manually refresh test metrics
   */
  async refreshMetrics(testId: string): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post(`/ab-tests/${testId}/refresh-metrics`);
    return response.data;
  },

  /**
   * Export test results to CSV
   */
  async exportCSV(testId: string): Promise<Blob> {
    const test = await this.get(testId);
    
    // Generate CSV content
    const headers = [
      'Variant Name',
      'Is Control',
      'Impressions',
      'Clicks',
      'Conversions',
      'CTR',
      'CVR',
      'Spend',
      'Revenue',
      'ROAS',
      'CPA',
      'Is Leader',
      'Is Winner'
    ];
    
    const rows = test.variants.map(v => [
      v.variant_name,
      v.is_control ? 'Yes' : 'No',
      v.impressions,
      v.clicks,
      v.conversions,
      (v.ctr * 100).toFixed(2) + '%',
      (v.cvr * 100).toFixed(2) + '%',
      '$' + v.spend.toFixed(2),
      '$' + v.revenue.toFixed(2),
      v.roas.toFixed(2) + 'x',
      '$' + v.cpa.toFixed(2),
      v.is_leader ? 'Yes' : 'No',
      v.is_winner ? 'Yes' : 'No'
    ]);
    
    const csv = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');
    
    return new Blob([csv], { type: 'text/csv' });
  },

  /**
   * Calculate sample size requirements
   */
  calculateSampleSize(params: {
    baseline_value: number;
    minimum_detectable_effect: number; // % lift (e.g., 20 for 20%)
    confidence_level: number; // 0.90, 0.95, 0.99
    statistical_power: number; // typically 0.80
  }): {
    required_sample_size_per_variant: number;
    estimated_duration_days: number;
    warnings: string[];
  } {
    const { baseline_value, minimum_detectable_effect, confidence_level, statistical_power } = params;
    
    // Z-score lookup for confidence levels
    const zAlpha: Record<number, number> = {
      0.90: 1.645,
      0.95: 1.96,
      0.99: 2.576
    };
    
    const zBeta = 0.842; // For 80% power
    
    // Calculate effect size
    const effectSize = (minimum_detectable_effect / 100) * baseline_value;
    const pooledStdDev = Math.sqrt(baseline_value * (1 - baseline_value));
    
    // Sample size formula for proportions
    const numerator = Math.pow(zAlpha[confidence_level] + zBeta, 2) * 2 * Math.pow(pooledStdDev, 2);
    const denominator = Math.pow(effectSize, 2);
    const sampleSize = Math.ceil(numerator / denominator);
    
    // Estimate duration (assume 1000 impressions per day average)
    const estimatedDays = Math.ceil(sampleSize / 1000);
    
    const warnings: string[] = [];
    
    if (sampleSize > 10000) {
      warnings.push('Sample size is very large. Consider increasing minimum detectable effect.');
    }
    
    if (estimatedDays > 30) {
      warnings.push(`Test would take ${estimatedDays} days at average traffic. Consider increasing budget or MDE.`);
    }
    
    if (minimum_detectable_effect < 10) {
      warnings.push('Detecting effects smaller than 10% requires very large samples.');
    }
    
    return {
      required_sample_size_per_variant: sampleSize,
      estimated_duration_days: estimatedDays,
      warnings
    };
  }
};
